import React, { useEffect, useState } from 'react';
import ContractReportTemplate from '../../_components/templates/ContractReportTemplate';
import TabSelection from '../../_components/molecules/TabSelection';
import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  CardComponent,
  ReportOverviewCard,
  StandardsCard,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { useLocation, useNavigate } from 'react-router-dom';
import contractAIQConstants from '../../core-utils/messages/ContractAIQConstants';
import properties from '../../core-utils/properties';
import axios from 'axios';
import { useStores } from '../../stores';
import {
  getBarColor,
  getChipComponent,
  getReportBreadcrumbList,
  getRiskCategory,
  getStandardRiskValues,
} from '../../core-utils/helper-utils/contractAIQHelper';
import Loader from '../../_components/molecules/Loader';
import Divider from '../../_components/atoms/Divider';
import RecommendationInfo from '../../_components/atoms/RecommendationInfo';
import ClauseDetail from '../../_components/organisms/ClauseDetail';
import { checkOfListColumns } from '../ContractReportPage';

const PurchasingStandardsReport = () => {
  //styles
  const useStyles = makeStyles()({
    tabContent: {
      paddingTop: '24px',
    },
    overviewTab: {
      display: 'flex',
      flexDirection: 'column',
      gap: '32px',
    },
    standardsCards: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '16px',
    },
  });

  //constants
  const { classes } = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const breadcrumbs = getReportBreadcrumbList(navigate, location?.state);
  const { reportName, document }: any = location.state;
  const constants =
    contractAIQConstants.CONTRACT_REPORTS.PURCHASING_STANDARDS_REPORT;
  const employerGroupStore = useStores().employerGroupStore;
  const contractStore = useStores().contractAiqStore;

  //states
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<{ id: string; index?: number }>({
    id: 'summary',
    index: 0,
  });
  const [standards, setStandards] = useState<any>([]);
  const [selectedStandard, setSelectedStandard] = useState<any>({});
  const [overallScore, setOverallScore] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [loader] = useState<any>(<Loader />);

  //APIs
  const getPurchasingStandards = () => {
    const url =
      properties.aiqBaseUrl +
      properties.contractBaseUrl +
      properties.purchasingStandardsReport;
    return axios.post(url, {
      eg_nid: employerGroupStore.employerGroupResponseData.eg_nid,
      service_agreement_id: contractStore.serviceAgreementId,
      report_name: reportName,
    });
  };

  const getRiskReport = () => {
    const url =
      properties.aiqBaseUrl +
      properties.playbookBaseUrl +
      properties.riskReport;
    return axios.post(url, {
      eg_nid: employerGroupStore.employerGroupResponseData.eg_nid[0],
      playbook_id: contractStore.serviceAgreementId,
    });
  };

  const getAllAPIs = async () => {
    await Promise.allSettled([getPurchasingStandards(), getRiskReport()]).then(
      (responses: any) => {
        if (
          responses[0]?.status === 'fulfilled' &&
          responses[1]?.status === 'fulfilled'
        ) {
          const purchasingStandards =
            responses[0]?.value?.data?.data?.standardsData;
          const standards = responses[1]?.value?.data?.data?.[0]?.standards;

          setOverallScore(responses[0]?.value?.data?.data?.overallScore);

          const standardsMap = standards.reduce((map: any, standard: any) => {
            map[standard.standard_id] = standard.standard_name;
            return map;
          }, {});

          const mergedData = purchasingStandards.map((item: any) => ({
            ...item,
            standard_name: standardsMap[item.standard],
          }));

          setStandards(mergedData);
          setLoading(false);
        }
      },
    );
  };

  //functions
  const handleReportSummary = () => {
    setActiveTab({ id: 'summary', index: 0 });
  };

  const handleDownloadReport = () => {};

  //function to get content of report summary tabs based on tab index
  const getTabContent = () => {
    switch (selectedTabIndex) {
      case 0:
        return <OverviewTab />;
      case 1:
        return <StandardsTab />;
      default:
        return <></>;
    }
  };

  //function to get content on the right based on left tab selection
  const getActiveTabContent = () => {
    switch (activeTab.id) {
      case 'summary':
        return <ReportSummaryContent />;
      case 'selected-standard':
        return <SelectedStandard />;
      default:
        return <></>;
    }
  };

  //useEffects
  useEffect(() => {
    getAllAPIs();
  }, []);

  //components
  //component for list of Standards on the left
  const Standards = () => {
    return (
      <Grid>
        {standards?.map((item: any, index: number) => {
          return (
            <ReportOverviewCard
              title={item?.standard}
              value={item?.score}
              handleOnClick={() => {
                setActiveTab({
                  id: 'selected-standard',
                  index: index + 1,
                });
                setSelectedStandard(item);
              }}
              isActive={activeTab.index === index + 1}
              rootStyles={{
                gap: '0.5rem',
                height: '90px',
                width: '410px',
                padding: '8px 20px',
                flexWrap: 'nowrap',
              }}
              barColor={getBarColor(item?.score)}
              tooltip={item?.standard_name?.definition}
            />
          );
        })}
      </Grid>
    );
  };

  //component for report summary content
  const ReportSummaryContent = () => {
    return (
      <>
        <TabSelection
          selectedTabIndex={selectedTabIndex}
          setSelectedTabIndex={setSelectedTabIndex}
          tabList={constants.SUMMARY_TABS}
        />
        <Grid className={classes.tabContent}>{getTabContent()}</Grid>
      </>
    );
  };

  //components for repetitive Typography
  const getHeadingTypography = (label: string, margin?: string) => {
    return (
      <Typography
        fontSize="16px"
        fontType="semiBold"
        lineHeight="140%"
        textColor={patientAccessTheme.palette.text.highemphasis}
        margin={margin}
      >
        {label}
      </Typography>
    );
  };

  const getSubHeadingTypography = (label: string, bold?: boolean) => {
    return (
      <Typography
        fontSize="14px"
        fontType={bold ? 'semiBold' : 'regular'}
        lineHeight="24px"
        textColor={patientAccessTheme.palette.text.mediumemphasis}
        display="inline"
      >
        {label}
      </Typography>
    );
  };

  //component for overview tab content
  const OverviewTab = () => {
    return (
      <Grid className={classes.overviewTab}>
        <Grid>
          {getHeadingTypography(constants.SUMMARY.TITLE, '0px 0px 12px 0px')}
          <Grid>
            {getSubHeadingTypography(constants.SUMMARY.SUBTITLE.PART1)}
            {getSubHeadingTypography(document, true)}
            {getSubHeadingTypography(constants.SUMMARY.SUBTITLE.PART2)}
            {getSubHeadingTypography(`${overallScore}%`, true)}
            {getSubHeadingTypography(constants.SUMMARY.SUBTITLE.PART3)}
          </Grid>
        </Grid>
        <Grid>
          {getHeadingTypography(constants.STANDARDS.TITLE, '0px 0px 12px 0px')}
          <Grid className={classes.standardsCards}>
            {standards?.map((standard: any, index: number) => {
              return (
                <StandardsCard
                  title={standard?.standard}
                  percentage={`${standard?.score}%`}
                  riskType={getRiskCategory(standard?.score)}
                  valuesTitle={'Clauses at:'}
                  values={getStandardRiskValues(standard)}
                  clickable
                  handleOnClick={() => {
                    setActiveTab({
                      id: 'selected-standard',
                      index: index + 1,
                    });
                    setSelectedStandard(standard);
                  }}
                />
              );
            })}
          </Grid>
        </Grid>
      </Grid>
    );
  };

  //component for standards tab content
  const StandardsTab = () => {
    return (
      <>
        {standards?.map((standard: any) => (
          <Grid style={{ marginBottom: '16px' }}>
            <CardComponent
              padding="16px"
              width="100%"
              title={
                <Typography
                  fontSize="14px"
                  fontType={'medium'}
                  lineHeight="24px"
                  textColor={patientAccessTheme.palette.text.highemphasis}
                >
                  {standard?.standard}
                </Typography>
              }
              titleSubTitleMargin="4px"
              body={getSubHeadingTypography(
                standard?.standard_name?.definition,
              )}
            />
          </Grid>
        ))}
      </>
    );
  };

  //component for list of clauses for a standard
  const ListOfClauses = (standard: any) => {
    const clauses: any[] = standard?.clauses;

    return (
      <Grid>
        {clauses?.length > 0 && (
          <>
            {clauses?.map((clause: any) => {
              const tableData: any[] = [];
              clause?.metlist?.map((item: any) => {
                const eachRow: any[] = [];
                eachRow[0] = item?.category;
                eachRow[1] = item?.parameter;
                eachRow[2] = getChipComponent('Fully Met');
                tableData.push(eachRow);
              });
              clause?.notmetlist?.map((item: any) => {
                const eachRow: any[] = [];
                eachRow[0] = item?.category;
                eachRow[1] = item?.parameter;
                eachRow[2] = getChipComponent('Not Met');
                tableData.push(eachRow);
              });
              return (
                <Grid style={{ marginBottom: '12px' }}>
                  <ClauseDetail
                    title={clause?.clause_name}
                    analysis={clause?.analysis}
                    metCount={clause?.metlist?.length}
                    unMetCount={clause?.notmetlist?.length}
                    chipValue={getRiskCategory(clause?.score)}
                    columns={checkOfListColumns}
                    rowsData={tableData}
                    tooltip={clause?.aim_of_the_clause}
                  />
                </Grid>
              );
            })}
          </>
        )}
      </Grid>
    );
  };

  //   component for selected standard
  const SelectedStandard = () => {
    return (
      <>
        <RecommendationInfo
          title={selectedStandard?.standard}
          subTitle={selectedStandard?.standard_name?.definition}
          objectives={Object.values(selectedStandard?.standard_name?.objective)}
          tooltip={selectedStandard?.standard_name?.importance}
        />
        <Divider marginBottom="32px" marginTop="32px" />
        <ReportOverviewCard
          rootStyles={{
            width: '100%',
            background: 'none',
            padding: '0px',
            borderBottom: 'none',
            cursor: 'none',
            height: 'auto',
          }}
          title={selectedStandard?.standard}
          value={selectedStandard?.score}
          barColor={getBarColor(selectedStandard?.score)}
        />
        <Divider marginBottom="32px" marginTop="32px" />
        {ListOfClauses(selectedStandard)}
      </>
    );
  };

  return (
    <>
      {loading ? (
        loader
      ) : (
        <ContractReportTemplate
          breadcrumbsList={breadcrumbs}
          reportName={`${reportName} of`}
          document={document}
          handleDocumentOnclick={() => {}}
          handleDownloadReport={handleDownloadReport}
          handleReportSummary={handleReportSummary}
          isReportSummaryActive={activeTab.id === 'summary'}
          leftContent={<Standards />}
          rightContent={getActiveTabContent()}
        />
      )}
    </>
  );
};

export default PurchasingStandardsReport;
